import { Box } from '@mui/material';
import React from 'react';
import { groupIssueTypesByCategory } from '../../constants/issueTypes';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import {
  getIssueChipColors,
  ISSUE_CATEGORY_LABELS,
} from '../../utils/issueChipStyle';

interface IssueTypeChipsProps {
  /** Receives the WIRE value, never the display label. */
  onSelect: (issueType: string) => void;
}

/**
 * The whole taxonomy, grouped by category, every value visible at once.
 *
 * ## Why no ranking and no drill-in
 *
 * The list is deliberately STABLE. A ranked list reshuffles between flags and defeats
 * muscle memory; a fixed one lets an agent learn where each value lives. Grouping is
 * what does the work here — a flat 19-item list is exactly what buried
 * `Unnecessary Staff Intervention` at the bottom of the Slack form's dropdown despite
 * it being ~52% of all reports. It is now the first chip of the first group.
 *
 * Row order comes from `IssueCategory` declaration order via
 * `groupIssueTypesByCategory`; colour comes from the category. Nothing here sorts.
 */
const IssueTypeChips = ({ onSelect }: IssueTypeChipsProps) => (
  <Box
    sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
    data-testid="issue-type-chips"
  >
    {groupIssueTypesByCategory().map(({ category, types }) => (
      <Box
        key={category}
        // Grouped and named, so a screen reader announces which family a chip
        // belongs to — the heading beside it is visual-only.
        role="group"
        aria-label={ISSUE_CATEGORY_LABELS[category]}
        sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}
      >
        <Box
          component="span"
          sx={{
            flex: '0 0 92px',
            color: 'rgba(255,255,255,0.62)',
            fontSize: '0.62rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            textAlign: 'right',
            pt: '4px',
          }}
        >
          {ISSUE_CATEGORY_LABELS[category]}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px', flex: 1 }}>
          {types.map(({ value }) => {
            const { backgroundColor, color } = getIssueChipColors(category);
            return (
              <Box
                key={value}
                component="button"
                type="button"
                // The WIRE value, so a display-label change can never alter what is
                // recorded.
                onClick={() => onSelect(value)}
                data-issue-type={value}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  backgroundColor,
                  color,
                  borderRadius: '3px',
                  px: '7px',
                  py: '3px',
                  fontSize: HITL3_FONT_SIZE,
                  lineHeight: 1.25,
                  whiteSpace: 'nowrap',
                  // No border, ring or outline on HOVER — HITL has no such idiom,
                  // and a ring would also reflow a wrapped chip row. A brightness
                  // shift reads as "pressable" and costs no layout.
                  transition: 'filter 0.12s',
                  '&:hover': { filter: 'brightness(0.92)' },
                  // KEYBOARD focus is a different job and does need a visible ring:
                  // `all: unset` above resets `outline`, so tabbing through the chips
                  // moved focus invisibly. `:focus-visible` (not `:focus`) keeps it
                  // off mouse presses. `outline` rather than a border or box-shadow
                  // because it is drawn outside the box and cannot reflow the row.
                  '&:focus-visible': {
                    outline: `2px solid ${Colors.white}`,
                    outlineOffset: '2px',
                  },
                }}
              >
                {value}
              </Box>
            );
          })}
        </Box>
      </Box>
    ))}
  </Box>
);

/**
 * MEMOIZED, and `onSelect` must therefore be stable (it is — `useCallback` in
 * IssueFlagPanel). Without this, every keystroke in the panel's detail field
 * re-rendered all 19 chips, and each chip's `sx` object is re-created and
 * re-serialized by emotion on every render — enough to make typing visibly lag.
 */
export default React.memo(IssueTypeChips);
